import { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../supabase'
import { lire } from './erreurs'
import { montant } from './format'
import { camionsDe, ORDER_STATUS } from './carnetUtils'
import {
  t, Stagger, Panel, PanelHead, PanelTitle, PageHead, PageTitle, PageSub,
  StatGrid, Tile, ChipBar, Chip, Loading, Empty, Muted, Pill,
  TableWrap, Tableau, useUI,
} from './ui'

/* Ce qui est fait, et ce qui vient. La distinction commande toute la
   page : le réalisé est un constat, le prévisionnel une hypothèse. */
const A_VENIR = ['validated', 'scheduled']

const Colonnes = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px; margin-bottom: 18px;
  @media (max-width: 860px) { grid-template-columns: 1fr; }
`
const Bloc = styled.div`
  border: 1px solid ${(p) => (p.$avenir ? '#D3E4F3' : t.line)};
  border-radius: ${t.r};
  background: ${(p) => (p.$avenir ? t.blueWash : t.surface)};
  padding: 15px 17px;
`
const BlocTitre = styled.div`
  display: flex; align-items: baseline; gap: 8px;
  font-family: ${t.font}; font-size: 0.65rem; font-weight: 600;
  letter-spacing: 0.08em; text-transform: uppercase;
  color: ${(p) => (p.$avenir ? t.blue : t.textFaint)};
  margin-bottom: 10px;
`
const BlocLigne = styled.div`
  display: flex; justify-content: space-between; align-items: baseline;
  gap: 12px; padding: 4px 0;
  font-family: ${t.font}; font-size: 0.82rem; color: ${t.textSoft};
  b { color: ${t.text}; font-weight: 600; font-variant-numeric: tabular-nums; }
`
const BlocTotal = styled(BlocLigne)`
  margin-top: 8px; padding-top: 9px;
  border-top: 1px solid ${(p) => (p.$avenir ? '#C4DBF0' : t.line)};
  span { font-weight: 600; color: ${t.text}; }
  b {
    font-family: ${t.serif}; font-size: 1.14rem; font-weight: 650;
    letter-spacing: -0.03em;
    color: ${(p) => (p.$positif ? t.green : t.red)};
  }
`
const Gain = styled.span`
  font-weight: 650; font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$positif ? t.green : t.red)};
`
const SansDate = styled.span`
  font-style: italic; color: ${t.textFaint};
`

/* Agrège les coûts figés d'un lot de commandes. Une commande sans
   calcul n'est comptée nulle part : elle serait sinon une marge de
   100 %, ce qui fausserait tout vers le haut. */
function agreger(voyages) {
  const chiffres = voyages.filter((v) => v.cout)
  const somme = (f) => chiffres.reduce((s, v) => s + (f(v) || 0), 0)
  const ca = somme((v) => v.ca)
  const cout = somme((v) => v.cout.total)
  return {
    nb: voyages.length,
    nbChiffres: chiffres.length,
    ca,
    achat: somme((v) => v.cout.achat),
    carburant: somme((v) => (v.cout.carburant_appro || 0) + (v.cout.carburant_livraison || 0)),
    rplp: somme((v) => v.cout.rplp),
    taxe: somme((v) => v.cout.taxe),
    fourrage: somme((v) => v.cout.taxe_fourrage),
    cout,
    marge: ca - cout,
    tonnage: voyages.reduce((s, v) => s + v.tonnage, 0),
    camions: voyages.reduce((s, v) => s + v.camions, 0),
    caTotal: voyages.reduce((s, v) => s + v.ca, 0),
  }
}

export default function Previsionnel() {
  const navigate = useNavigate()
  const { toast } = useUI()
  const [voyages, setVoyages] = useState([])
  const [loading, setLoading] = useState(true)
  const [tri, setTri] = useState('date')

  useEffect(() => {
    (async () => {
      const commandes = await lire(
        supabase.from('orders')
          .select('id, quote_id, status, type, date_livraison, poids_total_t, nb_camions_total, lignes, ca_total_chf, ca_compta_chf, cout_revient, clients(name, canton)')
          .in('status', [...A_VENIR, 'delivered']),
        toast, 'chargement du prévisionnel',
      )
      if (!commandes) return setLoading(false)

      /* Les devis portent aussi un coût figé, pour les commandes créées
         avant que le calcul n'existe sur la commande elle-même. */
      const idsDevis = commandes.filter((o) => !o.cout_revient && o.quote_id).map((o) => o.quote_id)
      let coutsDevis = {}
      if (idsDevis.length) {
        const devis = await lire(
          supabase.from('quotes').select('id, cout_revient').in('id', idsDevis).not('cout_revient', 'is', null),
          toast, 'chargement des coûts',
        )
        coutsDevis = Object.fromEntries((devis || []).map((q) => [q.id, q.cout_revient]))
      }

      setVoyages(commandes.map((o) => ({
        id: o.id,
        statut: o.status,
        type: o.type,
        date: o.date_livraison,
        client: o.clients?.name || 'client inconnu',
        canton: o.clients?.canton || '',
        produits: (o.lignes || []).map((l) => l.produit).join(', ') || '—',
        tonnage: o.poids_total_t || 0,
        camions: camionsDe(o),
        ca: o.ca_compta_chf ?? o.ca_total_chf ?? 0,
        surCompta: o.ca_compta_chf != null,
        cout: o.cout_revient || coutsDevis[o.quote_id] || null,
      })))
      setLoading(false)
    })()
  }, [toast])

  const realise = useMemo(() => agreger(voyages.filter((v) => v.statut === 'delivered')), [voyages])
  const avenir = useMemo(() => agreger(voyages.filter((v) => A_VENIR.includes(v.statut))), [voyages])

  const lignes = useMemo(() => {
    const l = voyages.filter((v) => A_VENIR.includes(v.statut))
    if (tri === 'marge') {
      return [...l].sort((a, b) => {
        const ma = a.cout ? a.ca - a.cout.total : -Infinity
        const mb = b.cout ? b.ca - b.cout.total : -Infinity
        return mb - ma
      })
    }
    if (tri === 'ca') return [...l].sort((a, b) => b.ca - a.ca)
    /* Par date, les livraisons sans date fermée passent en dernier :
       ce sont des ventes acquises mais non encore calées. */
    return [...l].sort((a, b) => {
      if (!a.date && !b.date) return 0
      if (!a.date) return 1
      if (!b.date) return -1
      return new Date(a.date) - new Date(b.date)
    })
  }, [voyages, tri])

  if (loading) return <Loading rows={4} h="90px" />

  const cumul = {
    ca: realise.ca + avenir.ca,
    marge: realise.marge + avenir.marge,
  }
  const sansCout = avenir.nb - avenir.nbChiffres

  return (
    <>
      <PageHead>
        <div>
          <PageTitle>Prévisionnel</PageTitle>
          <PageSub>
            {realise.nb} livraison(s) faites · {avenir.nb} à venir ·
            {' '}{avenir.tonnage.toFixed(1)} T et {avenir.camions} camion(s) engagés
          </PageSub>
        </div>
      </PageHead>

      <StatGrid style={{ marginBottom: 18 }}>
        <Tile i={0} label="Marge réalisée" value={montant(realise.marge, { signe: true })}
          hint={`sur ${montant(realise.ca)} encaissés`} tone={realise.marge >= 0 ? 'green' : 'red'} />
        <Tile i={1} label="Marge prévisionnelle" value={montant(avenir.marge, { signe: true })}
          hint={`sur ${montant(avenir.ca)} engagés`} tone="blue" />
        <Tile i={2} label="Projection" value={montant(cumul.marge, { signe: true })}
          hint={`${montant(cumul.ca)} de chiffre au total`} tone="gold" />
        <Tile i={3} label="Marge au camion"
          value={avenir.camions > 0 ? montant(avenir.marge / avenir.camions) : '—'}
          hint={`${avenir.camions} camion(s) à charger`} tone="teal" />
      </StatGrid>

      <Colonnes>
        {[
          { titre: 'Réalisé — livraisons faites', d: realise, avenir: false },
          { titre: 'Prévisionnel — validé et planifié', d: avenir, avenir: true },
        ].map(({ titre, d, avenir: futur }) => (
          <Stagger key={titre} $i={futur ? 1 : 0}>
            <Bloc $avenir={futur}>
              <BlocTitre $avenir={futur}>
                {titre}
                <Pill $tone={futur ? 'blue' : 'neutral'} style={{ marginLeft: 'auto' }}>
                  {d.nb}
                </Pill>
              </BlocTitre>
              <BlocLigne><span>Chiffre d'affaires</span><b>{montant(d.ca)}</b></BlocLigne>
              <BlocLigne><span>Achat marchandise</span><b>−{montant(d.achat)}</b></BlocLigne>
              <BlocLigne><span>Carburant</span><b>−{montant(d.carburant)}</b></BlocLigne>
              <BlocLigne><span>RPLP</span><b>−{montant(d.rplp)}</b></BlocLigne>
              <BlocLigne><span>Taxe sur le facturé</span><b>−{montant(d.taxe)}</b></BlocLigne>
              {d.fourrage > 0 && (
                <BlocLigne><span>Taxe fourrage</span><b>−{montant(d.fourrage)}</b></BlocLigne>
              )}
              <BlocTotal $avenir={futur} $positif={d.marge >= 0}>
                <span>Résultat net</span><b>{montant(d.marge, { signe: true })}</b>
              </BlocTotal>
              <Muted style={{ fontSize: '0.72rem', marginTop: 7, display: 'block' }}>
                {d.nbChiffres} sur {d.nb} chiffrée(s)
                {d.ca > 0 && ` · ${(d.marge / d.ca * 100).toFixed(1)} % de marge`}
              </Muted>
            </Bloc>
          </Stagger>
        ))}
      </Colonnes>

      <Stagger $i={2}>
        <Panel>
          <PanelHead>
            <PanelTitle>Détail du prévisionnel</PanelTitle>
            <ChipBar>
              <Chip $on={tri === 'date'} onClick={() => setTri('date')}>Par date</Chip>
              <Chip $on={tri === 'ca'} onClick={() => setTri('ca')}>Par CA</Chip>
              <Chip $on={tri === 'marge'} onClick={() => setTri('marge')}>Par marge</Chip>
            </ChipBar>
          </PanelHead>

          {sansCout > 0 && (
            <Muted style={{ fontSize: '0.76rem', margin: '0 0 12px', display: 'block' }}>
              {sansCout} commande(s) à venir sans coût de revient figé : leur chiffre
              d'affaires est affiché, mais elles ne comptent dans aucun total de marge.
              Le calcul se lance depuis la fiche de la commande.
            </Muted>
          )}

          {lignes.length === 0 ? (
            <Empty icon="📈" title="Rien d'engagé">
              Les commandes validées ou planifiées apparaîtront ici avec leur marge attendue.
            </Empty>
          ) : (
            <TableWrap>
              <Tableau $min="980px">
                <thead>
                  <tr>
                    <th>Livraison</th><th>Client</th><th>Marchandise</th>
                    <th className="num">T</th><th className="num">Cam.</th>
                    <th className="num">CA</th><th className="num">CA / camion</th>
                    <th className="num">Marchandise</th><th className="num">Carburant</th>
                    <th className="num">RPLP</th><th className="num">Taxes</th>
                    <th className="num">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((v) => {
                    const c = v.cout
                    const marge = c ? v.ca - c.total : null
                    return (
                      <tr key={v.id} style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/admin/carnet/${v.id}`)}>
                        <td>
                          {v.date
                            ? new Date(v.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })
                            : <SansDate>sans date</SansDate>}
                          <div style={{ fontSize: '0.68rem', color: t.textFaint }}>
                            {ORDER_STATUS[v.statut]?.label.replace(/^\S+\s/, '')}
                          </div>
                        </td>
                        <td className="fort">
                          {v.client}
                          {v.canton && <div style={{ fontSize: '0.68rem', color: t.textFaint }}>{v.canton}</div>}
                        </td>
                        <td>{v.produits}</td>
                        <td className="num">{v.tonnage || '—'}</td>
                        <td className="num">{v.camions}</td>
                        <td className="num fort">{montant(v.ca)}{v.surCompta ? '' : ' *'}</td>
                        <td className="num">{v.camions ? montant(v.ca / v.camions) : '—'}</td>
                        <td className="num">{c ? `−${montant(c.achat)}` : '—'}</td>
                        <td className="num">{c ? `−${montant((c.carburant_appro || 0) + (c.carburant_livraison || 0))}` : '—'}</td>
                        <td className="num">{c ? `−${montant(c.rplp)}` : '—'}</td>
                        <td className="num">
                          {c ? `−${montant((c.taxe || 0) + (c.taxe_fourrage || 0))}` : '—'}
                        </td>
                        <td className="num">
                          {marge == null
                            ? <Muted style={{ fontSize: '0.72rem' }}>non chiffré</Muted>
                            : <Gain $positif={marge >= 0}>{montant(marge, { signe: true })}</Gain>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3}>Total prévisionnel</td>
                    <td className="num">{avenir.tonnage.toFixed(1)}</td>
                    <td className="num">{avenir.camions}</td>
                    <td className="num">{montant(avenir.caTotal)}</td>
                    <td className="num">
                      {avenir.camions ? montant(avenir.caTotal / avenir.camions) : '—'}
                    </td>
                    <td className="num">−{montant(avenir.achat)}</td>
                    <td className="num">−{montant(avenir.carburant)}</td>
                    <td className="num">−{montant(avenir.rplp)}</td>
                    <td className="num">−{montant(avenir.taxe + avenir.fourrage)}</td>
                    <td className="num">
                      <Gain $positif={avenir.marge >= 0}>{montant(avenir.marge, { signe: true })}</Gain>
                    </td>
                  </tr>
                </tfoot>
              </Tableau>
            </TableWrap>
          )}

          <Muted style={{ fontSize: '0.72rem', marginTop: 10, display: 'block' }}>
            Le CA retenu est le montant compta quand il est saisi ; un astérisque signale
            une commande où le total du devis a été repris à défaut. Les livraisons sans
            date fermée figurent en fin de liste : la vente est acquise, le jour reste à caler.
          </Muted>
        </Panel>
      </Stagger>
    </>
  )
}
